import Cobrand from '@/components/Cobrand'
import Layout from '@/components/Layout'
import Meta from '@/components/Meta'
import Packages from '@/components/Packages'
import TmsScript from '@/components/TmsScript/TmsScript'
import DEFAULT_META_TAGS from '@/constants/meta'
import useCanonicalUrl from '@/hooks/useCanonicalUrl'
import type { ContentPageProps, CustomContext } from '@/types'
import { ContentfulProvider } from '@pcln/contentful-components'
import { FeatureProvider } from '@pcln/feature-components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { type GetServerSideProps } from 'next'
import { useState } from 'react'
import setiService from '../server/middleware/seti'
import prepopulatePackagesValues from '../server/prepop/packages'
import getContentfulData from '../server/utils/get-contentful-data'
import oktaJwtVerify from '../server/utils/okta-jwt-verifier'
import fetchPCLNHeaderFooterContent from '../server/utils/pcln-header-footer'
import retrieveContentfulSecrets from '../server/utils/retrieve-contentful-secrets'
import generateBootstrapData from '../utils/generate-bootstrap-data'
import mapSetiResponse from '../utils/map-seti-response'

function VacationPackages({ content, headerHTML, footerHTML, bootstrapData }: ContentPageProps) {
  const canonicalUrl = useCanonicalUrl()
  const [queryClient] = useState(() => new QueryClient())
  return (
    <>
      <Meta
        description={DEFAULT_META_TAGS.description.vacationpackages}
        pageTitle={DEFAULT_META_TAGS.title.vacationpackages}
        canonicalUrl={canonicalUrl}
      />
      <TmsScript product='packages' />
      <FeatureProvider features={bootstrapData.featureToggles}>
        <ContentfulProvider contentful={content}>
          <QueryClientProvider client={queryClient}>
            <Layout headerHTML={headerHTML} footerHTML={footerHTML}>
              <Cobrand />
              <Packages />
            </Layout>
          </QueryClientProvider>
        </ContentfulProvider>
      </FeatureProvider>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<ContentPageProps> = async (ctx) => {
  const { req, res } = ctx as CustomContext

  const bootstrapData = generateBootstrapData({ product: 'packages', req, res })
  const { webstats, referral, appVersion } = bootstrapData
  const { clientGUID: cguid } = webstats
  const { contentfulDeliverySecret, contentfulPreviewSecret } = retrieveContentfulSecrets()
  const isContentfulEnabled = res.locals.features?.siteExperience.isContentfulEnabled ?? true
  const [pclnHeaderFooterContent, signInStatus, { content, experimentNames }, packagesPrepopData] =
    await Promise.all([
      fetchPCLNHeaderFooterContent(req, res),
      oktaJwtVerify({ authHdr: req.headers.authorization ?? '', cguid }),
      getContentfulData({
        contentTypeId: 'pageLanding',
        contentfulDeliverySecret,
        contentfulPreviewSecret,
        product: 'Packages',
        contentPreview: ctx.query['content-preview'] === 'true',
        isContentfulEnabled,
      }),
      prepopulatePackagesValues(ctx.query),
    ])

  const setiResponseEvents = await setiService(req, {
    appVersion,
    allowSetiOverrides: true,
    webstats,
    referral,
    reqQueryParams: ctx.query,
    ...(experimentNames && {
      additionalExperiments: experimentNames,
    }),
  })
  const setiMap = mapSetiResponse(setiResponseEvents)
  const isAdvancedSearchEnabled = setiMap['PKG_ERROR_PERSIST_ADVANCED_SEARCH'] === 'VARIANT'

  const redirectSource = ctx.query['redirect-source']
  const isRedirectSourceError = typeof redirectSource === 'string' && redirectSource.toLowerCase() === 'error'

  const hasAdvancedSearchCookie = (req.headers.cookie ?? '').includes('pkgAdvancedSearch=true')

  if (isAdvancedSearchEnabled && isRedirectSourceError && hasAdvancedSearchCookie) {
    const message = typeof ctx.query['message'] === 'string' ? ctx.query['message'] : undefined
    const destination = `/shop/search?redirect-source=error${message ? `&message=${encodeURIComponent(message)}` : ''}`
    return { redirect: { destination, permanent: false } }
  }

  const prePopData = packagesPrepopData ? { packages: packagesPrepopData } : null

  const bootstrapWithJwtSignInData = {
    ...bootstrapData,
    customer: {
      signedIn: signInStatus.signedIn ?? false,
      emailAddress: signInStatus.email ?? '',
      firstName: String(signInStatus.firstName ?? ''),
    },
  }

  return {
    props: {
      content,
      ...pclnHeaderFooterContent,
      bootstrapData: {
        ...bootstrapWithJwtSignInData,
        fullSETI: setiResponseEvents,
        seti: setiMap,
        signInStatus,
        ...(prePopData && { prePopData }),
      },
    },
  }
}

export default VacationPackages
